<script setup lang="ts">
import { computed } from "vue";
import { Circle, CircleCheck } from "@lucide/vue";
import { Button } from "@/shadcn/components/ui/button";
import type { ImportRequirement } from "./types";

const props = defineProps<{
  requirements: ImportRequirement[];
  /** Rows the import will go through, when they could be counted. */
  rows: number | null;
}>();

const missingCount = computed(() => props.requirements.filter(requirement => !requirement.met).length);
</script>

<template>
  <aside class="tw:rounded-xl tw:border tw:border-zinc-200 tw:bg-white tw:p-5 tw:lg:sticky tw:lg:top-4">
    <h3 class="tw:m-0 tw:text-base tw:font-semibold">{{ $t("Before importing") }}</h3>

    <ul class="tw:mt-4 tw:mb-0 tw:list-none tw:space-y-2 tw:p-0 tw:text-sm">
      <li v-for="requirement in requirements" :key="requirement.label" class="tw:flex tw:items-start tw:gap-2">
        <CircleCheck v-if="requirement.met" class="tw:mt-0.5 tw:size-4 tw:shrink-0 tw:text-green-600" />
        <Circle v-else class="tw:mt-0.5 tw:size-4 tw:shrink-0 tw:text-amber-500" />
        <span :class="requirement.met ? 'tw:text-zinc-600' : 'tw:font-medium tw:text-zinc-900'">{{ requirement.label }}</span>
      </li>
    </ul>

    <slot />

    <Button type="submit" class="tw:mt-5 tw:w-full" :disabled="missingCount > 0">
      <template v-if="rows !== null">
        {{ $t("Import {count} rows", { count: rows.toLocaleString() }) }}
      </template>
      <template v-else>{{ $t("Import") }}</template>
    </Button>
    <p v-if="missingCount > 0" class="tw:mt-2 tw:mb-0 tw:text-center tw:text-xs tw:text-zinc-500">
      {{ $t("Complete the steps above to start the import.") }}
    </p>
  </aside>
</template>
